// src/data/industrySections.js

export const industrySections = {
  // ============================================
  // NATURAL LANGUAGE PROCESSING (NLP)
  // ============================================
  nlp: {
    id: 'nlp',
    name: 'Natural Language Processing',
    icon: '🗣️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'NLP Engineer with 5+ years of experience in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'Java', 'C++', 'R'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'nlp-libraries',
        label: 'NLP Libraries & Frameworks',
        type: 'skills',
        icon: '📚',
        defaultSkills: ['Hugging Face Transformers', 'spaCy', 'NLTK', 'Gensim', 'Stanford CoreNLP'],
        placeholder: 'Add library...',
        required: true
      },
      {
        id: 'ml-frameworks',
        label: 'Machine Learning Frameworks',
        type: 'skills',
        icon: '⚡',
        defaultSkills: ['PyTorch', 'TensorFlow', 'Scikit-learn', 'Keras'],
        placeholder: 'Add framework...',
        required: true
      },
      {
        id: 'llms',
        label: 'Large Language Models (LLMs)',
        type: 'skills',
        icon: '🤖',
        defaultSkills: ['BERT', 'GPT', 'T5', 'LLaMA', 'Prompt Engineering', 'Fine-tuning'],
        placeholder: 'Add LLM...',
        required: false
      },
      {
        id: 'data-tools',
        label: 'Data Tools & Databases',
        type: 'skills',
        icon: '🗄️',
        defaultSkills: ['SQL', 'MongoDB', 'Pinecone', 'Chroma', 'Milvus', 'Git'],
        placeholder: 'Add data tool...',
        required: false
      },
      {
        id: 'deployment-cloud',
        label: 'Deployment & Cloud',
        type: 'skills',
        icon: '☁️',
        defaultSkills: ['Docker', 'Kubernetes', 'AWS', 'GCP', 'FastAPI'],
        placeholder: 'Add deployment tool...',
        required: false
      },
      {
        id: 'nlp-projects',
        label: 'NLP Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'technologies', 'outcome'],
        required: false
      },
      {
        id: 'publications',
        label: 'Publications & Research',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // PREDICTIVE ANALYTICS
  // ============================================
  predictiveAnalytics: {
    id: 'predictiveAnalytics',
    name: 'Predictive Analytics',
    icon: '📈',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Predictive Analytics Specialist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'R', 'SQL', 'SAS', 'Julia'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'statistical-methods',
        label: 'Statistical Methods & Techniques',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Regression Analysis', 'Time Series', 'Monte Carlo', 'Bayesian Inference', 'Hypothesis Testing'],
        placeholder: 'Add statistical method...',
        required: true
      },
      {
        id: 'ml-algorithms',
        label: 'Machine Learning Algorithms',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['Random Forest', 'XGBoost', 'LSTM', 'ARIMA', 'Prophet', 'Neural Networks'],
        placeholder: 'Add algorithm...',
        required: true
      },
      {
        id: 'data-tools',
        label: 'Data Tools & Platforms',
        type: 'skills',
        icon: '🗄️',
        defaultSkills: ['Tableau', 'Power BI', 'Looker', 'Snowflake', 'BigQuery', 'Redshift'],
        placeholder: 'Add data tool...',
        required: false
      },
      {
        id: 'industry-domains',
        label: 'Industry Domains',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Finance', 'Healthcare', 'Retail', 'Manufacturing', 'Supply Chain'],
        placeholder: 'Add industry domain...',
        required: false
      },
      {
        id: 'predictive-projects',
        label: 'Predictive Analytics Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'techniques', 'accuracy', 'businessImpact'],
        required: true
      }
    ]
  },

  // ============================================
  // BLOCKCHAIN / WEB3
  // ============================================
  blockchain: {
    id: 'blockchain',
    name: 'Blockchain / Web3',
    icon: '⛓️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Blockchain Developer with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Solidity', 'Rust', 'Go', 'JavaScript', 'Python', 'C++'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'blockchain-platforms',
        label: 'Blockchain Platforms',
        type: 'skills',
        icon: '⛓️',
        defaultSkills: ['Ethereum', 'Solana', 'Polygon', 'Avalanche', 'Polkadot', 'Hyperledger'],
        placeholder: 'Add blockchain platform...',
        required: true
      },
      {
        id: 'smart-contracts',
        label: 'Smart Contract Development',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['ERC-20', 'ERC-721', 'ERC-1155', 'OpenZeppelin', 'Hardhat', 'Truffle', 'Foundry'],
        placeholder: 'Add smart contract skill...',
        required: true
      },
      {
        id: 'web3-tools',
        label: 'Web3 Tools & Libraries',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Web3.js', 'Ethers.js', 'IPFS', 'The Graph', 'Chainlink', 'WalletConnect'],
        placeholder: 'Add Web3 tool...',
        required: false
      },
      {
        id: 'defi-nft',
        label: 'DeFi & NFT Experience',
        type: 'skills',
        icon: '💰',
        defaultSkills: ['DEX', 'Lending Protocols', 'Yield Farming', 'NFT Marketplaces', 'Minting'],
        placeholder: 'Add DeFi/NFT skill...',
        required: false
      },
      {
        id: 'blockchain-projects',
        label: 'Blockchain Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'blockchain', 'technologies', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // ROBOTICS
  // ============================================
  robotics: {
    id: 'robotics',
    name: 'Robotics',
    icon: '🤖',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Robotics Engineer with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['C++', 'Python', 'ROS', 'MATLAB', 'Java', 'C#'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'robotics-frameworks',
        label: 'Robotics Frameworks & Platforms',
        type: 'skills',
        icon: '⚙️',
        defaultSkills: ['ROS/ROS2', 'Gazebo', 'OpenCV', 'PCL', 'MoveIt', 'Webots'],
        placeholder: 'Add framework...',
        required: true
      },
      {
        id: 'control-systems',
        label: 'Control Systems & Algorithms',
        type: 'skills',
        icon: '🎮',
        defaultSkills: ['PID Control', 'MPC', 'Kalman Filters', 'SLAM', 'Path Planning', 'Kinematics'],
        placeholder: 'Add control system...',
        required: true
      },
      {
        id: 'sensors-hardware',
        label: 'Sensors & Hardware',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['LiDAR', 'RADAR', 'IMU', 'GPS', 'Cameras', 'Actuators', 'Embedded Systems'],
        placeholder: 'Add sensor/hardware...',
        required: false
      },
      {
        id: 'ai-ml-robotics',
        label: 'AI/ML for Robotics',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['Reinforcement Learning', 'Computer Vision', 'Deep Learning', 'Object Detection'],
        placeholder: 'Add AI/ML skill...',
        required: false
      },
      {
        id: 'robotics-projects',
        label: 'Robotics Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'roboticsPlatform', 'technologies', 'outcome'],
        required: true
      }
    ]
  },

  // ============================================
  // INTERNET OF THINGS (IoT)
  // ============================================
  iot: {
    id: 'iot',
    name: 'Internet of Things (IoT)',
    icon: '📶',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'IoT Engineer with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['C', 'C++', 'Python', 'JavaScript', 'Java', 'Rust'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'iot-platforms',
        label: 'IoT Platforms & Protocols',
        type: 'skills',
        icon: '📶',
        defaultSkills: ['MQTT', 'CoAP', 'Zigbee', 'LoRaWAN', 'BLE', 'AWS IoT Core', 'Azure IoT Hub'],
        placeholder: 'Add IoT platform/protocol...',
        required: true
      },
      {
        id: 'hardware-embedded',
        label: 'Hardware & Embedded Systems',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Arduino', 'Raspberry Pi', 'ESP32', 'STM32', 'ARM', 'FPGA', 'Sensors'],
        placeholder: 'Add hardware/embedded skill...',
        required: true
      },
      {
        id: 'cloud-data',
        label: 'Cloud & Data Processing',
        type: 'skills',
        icon: '☁️',
        defaultSkills: ['AWS', 'Azure', 'GCP', 'Kafka', 'Spark', 'Time Series DB', 'Data Pipelines'],
        placeholder: 'Add cloud/data skill...',
        required: false
      },
      {
        id: 'security',
        label: 'IoT Security',
        type: 'skills',
        icon: '🔒',
        defaultSkills: ['Encryption', 'Secure Boot', 'OTA Updates', 'Vulnerability Assessment', 'PKI'],
        placeholder: 'Add security skill...',
        required: false
      },
      {
        id: 'iot-projects',
        label: 'IoT Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'platform', 'technologies', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // EDGE COMPUTING
  // ============================================
  edgeComputing: {
    id: 'edgeComputing',
    name: 'Edge Computing',
    icon: '⚡',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Edge Computing Specialist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Go', 'Rust', 'Python', 'C++', 'JavaScript', 'Java'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'edge-platforms',
        label: 'Edge Platforms & Frameworks',
        type: 'skills',
        icon: '⚡',
        defaultSkills: ['AWS IoT Greengrass', 'Azure IoT Edge', 'Google IoT Core', 'KubeEdge', 'EdgeX Foundry'],
        placeholder: 'Add edge platform...',
        required: true
      },
      {
        id: 'cloud-architecture',
        label: 'Cloud & Distributed Architecture',
        type: 'skills',
        icon: '☁️',
        defaultSkills: ['Kubernetes', 'Docker', 'Serverless', 'Service Mesh', 'CDN', 'Microservices'],
        placeholder: 'Add cloud/architecture skill...',
        required: true
      },
      {
        id: 'networking',
        label: 'Networking & Protocols',
        type: 'skills',
        icon: '🌐',
        defaultSkills: ['TCP/IP', 'UDP', 'MQTT', 'WebRTC', 'RTSP', '5G', 'Network Security'],
        placeholder: 'Add networking skill...',
        required: false
      },
      {
        id: 'ai-ml-edge',
        label: 'AI/ML on Edge',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['TinyML', 'TensorFlow Lite', 'ONNX', 'Model Optimization', 'Inference at Edge'],
        placeholder: 'Add AI/ML skill...',
        required: false
      },
      {
        id: 'edge-projects',
        label: 'Edge Computing Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'platform', 'technologies', 'latencyReduction'],
        required: true
      }
    ]
  },

  // ============================================
  // QUANTUM COMPUTING
  // ============================================
  quantumComputing: {
    id: 'quantumComputing',
    name: 'Quantum Computing',
    icon: '🔬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Quantum Computing Researcher with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'Qiskit', 'Cirq', 'Q#', 'Julia', 'C++'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'quantum-platforms',
        label: 'Quantum Platforms & SDKs',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Qiskit', 'Cirq', 'Amazon Braket', 'Microsoft Quantum', 'QuEST', 'Strawberry Fields'],
        placeholder: 'Add quantum platform...',
        required: true
      },
      {
        id: 'quantum-algorithms',
        label: 'Quantum Algorithms',
        type: 'skills',
        icon: '🧮',
        defaultSkills: ['Shor\'s Algorithm', 'Grover\'s Algorithm', 'QAOA', 'VQE', 'QFT', 'Quantum Simulation'],
        placeholder: 'Add quantum algorithm...',
        required: true
      },
      {
        id: 'quantum-physics',
        label: 'Quantum Physics Concepts',
        type: 'skills',
        icon: '⚛️',
        defaultSkills: ['Quantum Mechanics', 'Qubits', 'Entanglement', 'Superposition', 'Quantum Gates', 'Decoherence'],
        placeholder: 'Add quantum physics concept...',
        required: true
      },
      {
        id: 'research-areas',
        label: 'Research Areas',
        type: 'skills',
        icon: '🔍',
        defaultSkills: ['Quantum Error Correction', 'Quantum Cryptography', 'Quantum Simulation', 'Quantum Machine Learning'],
        placeholder: 'Add research area...',
        required: false
      },
      {
        id: 'publications',
        label: 'Publications & Research',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'arxivId'],
        required: false
      }
    ]
  },

  // ============================================
  // SPACE TECHNOLOGY
  // ============================================
  spaceTech: {
    id: 'spaceTech',
    name: 'Space Technology',
    icon: '🚀',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Space Technology Engineer with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'C++', 'MATLAB', 'Java', 'Fortran', 'Ada'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'space-systems',
        label: 'Space Systems & Engineering',
        type: 'skills',
        icon: '🛰️',
        defaultSkills: ['Satellite Design', 'Spacecraft Operations', 'Orbital Mechanics', 'Astrodynamics', 'Attitude Control'],
        placeholder: 'Add space system skill...',
        required: true
      },
      {
        id: 'mission-engineering',
        label: 'Mission Engineering',
        type: 'skills',
        icon: '🎯',
        defaultSkills: ['Mission Planning', 'Launch Vehicles', 'Payload Integration', 'Telemetry', 'Ground Control'],
        placeholder: 'Add mission engineering skill...',
        required: true
      },
      {
        id: 'simulation-tools',
        label: 'Simulation & Modeling Tools',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['STK', 'GMAT', 'ANSYS', 'SolidWorks', 'CATIA', 'OpenFOAM'],
        placeholder: 'Add simulation tool...',
        required: false
      },
      {
        id: 'space-specializations',
        label: 'Space Specializations',
        type: 'skills',
        icon: '🌌',
        defaultSkills: ['Propulsion Systems', 'Thermal Control', 'Radiation Protection', 'Life Support', 'Space Habitats'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'space-projects',
        label: 'Space Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'missionType', 'technologies', 'outcome'],
        required: true
      }
    ]
  },

  // ============================================
  // NANOTECHNOLOGY
  // ============================================
  nanotechnology: {
    id: 'nanotechnology',
    name: 'Nanotechnology',
    icon: '🔬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Nanotechnology Researcher with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'MATLAB', 'R', 'C++', 'Fortran', 'Julia'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'nanomaterials',
        label: 'Nanomaterials & Applications',
        type: 'skills',
        icon: '🧪',
        defaultSkills: ['Carbon Nanotubes', 'Graphene', 'Nanoparticles', 'Quantum Dots', 'Nanocomposites', 'Nanocrystals'],
        placeholder: 'Add nanomaterial...',
        required: true
      },
      {
        id: 'characterization',
        label: 'Characterization Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['SEM', 'TEM', 'XRD', 'Raman Spectroscopy', 'AFM', 'NMR', 'Mass Spectrometry'],
        placeholder: 'Add characterization technique...',
        required: true
      },
      {
        id: 'fabrication',
        label: 'Fabrication & Synthesis',
        type: 'skills',
        icon: '🏭',
        defaultSkills: ['CVD', 'PVD', 'Lithography', 'Self-Assembly', 'Sol-Gel', 'Electrospinning'],
        placeholder: 'Add fabrication method...',
        required: true
      },
      {
        id: 'research-areas',
        label: 'Research Areas',
        type: 'skills',
        icon: '🔍',
        defaultSkills: ['Nanomedicine', 'Nanoelectronics', 'Energy Storage', 'Photovoltaics', 'Sensors', 'Biotechnology'],
        placeholder: 'Add research area...',
        required: false
      },
      {
        id: 'publications',
        label: 'Publications & Research',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'impactFactor'],
        required: false
      }
    ]
  },

  // ============================================
  // LOGISTICS & SUPPLY CHAIN
  // ============================================
  logistics: {
    id: 'logistics',
    name: 'Logistics & Supply Chain',
    icon: '🚚',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Logistics & Supply Chain Manager with experience in...',
        required: true
      },
      {
        id: 'supply-chain',
        label: 'Supply Chain Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Procurement', 'Demand Forecasting', 'Inventory Management', 'Warehousing', 'Distribution'],
        placeholder: 'Add supply chain skill...',
        required: true
      },
      {
        id: 'logistics-software',
        label: 'Logistics Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['SAP', 'Oracle SCM', 'Microsoft Dynamics', 'Blue Yonder', 'Manhattan WMS', 'Transportation Management'],
        placeholder: 'Add logistics software...',
        required: true
      },
      {
        id: 'analytics',
        label: 'Analytics & Optimization',
        type: 'skills',
        icon: '📈',
        defaultSkills: ['Data Analytics', 'Route Optimization', 'Inventory Optimization', 'ROI Analysis', 'KPI Tracking'],
        placeholder: 'Add analytics skill...',
        required: false
      },
      {
        id: 'transportation',
        label: 'Transportation & Freight',
        type: 'skills',
        icon: '✈️',
        defaultSkills: ['Freight Management', 'Customs Compliance', 'International Shipping', 'Last-Mile Delivery', 'Fleet Management'],
        placeholder: 'Add transportation skill...',
        required: false
      },
      {
        id: 'supply-chain-experience',
        label: 'Supply Chain Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CSCP', 'CPIM', 'CPSM', 'Six Sigma', 'Lean Management'],
        placeholder: 'Add certification...',
        required: false
      }
    ]
  },

  // ============================================
  // AVIATION / PILOT
  // ============================================
  pilot: {
    id: 'pilot',
    name: 'Aviation / Pilot',
    icon: '✈️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Experienced commercial pilot with...',
        required: true
      },
      {
        id: 'flying-hours',
        label: 'Flying Hours',
        type: 'flyingHours',
        icon: '⏱️',
        fields: ['totalHours', 'multiEngine', 'instrument', 'night', 'crossCountry', 'turbine'],
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications & Licenses',
        type: 'skills',
        icon: '🪪',
        defaultSkills: ['Commercial Pilot License', 'ATP', 'CFI', 'CFII', 'MEI', 'Type Ratings'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'aircraft-types',
        label: 'Aircraft Types Flown',
        type: 'skills',
        icon: '🛩️',
        defaultSkills: ['Boeing 737', 'Airbus A320', 'Cessna 172', 'Citation Jet'],
        placeholder: 'Add aircraft type...',
        required: true
      },
      {
        id: 'flight-experience',
        label: 'Flight Experience',
        type: 'experience',
        icon: '✈️',
        fields: ['airline', 'position', 'dates', 'aircraft', 'responsibilities'],
        required: true
      },
      {
        id: 'safety-training',
        label: 'Safety & Emergency Training',
        type: 'skills',
        icon: '🆘',
        defaultSkills: ['CRM', 'Emergency Procedures', 'Fire Fighting', 'First Aid', 'Water Survival'],
        placeholder: 'Add training...',
        required: false
      },
      {
        id: 'medical-certificate',
        label: 'Medical Certificate',
        type: 'text',
        icon: '🩺',
        placeholder: 'e.g., Class 1 Medical Certificate, valid until...',
        required: true
      }
    ]
  },

  // ============================================
  // SOFTWARE ENGINEER
  // ============================================
  softwareEngineer: {
    id: 'softwareEngineer',
    name: 'Software Engineer',
    icon: '💻',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Full-stack developer with...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['JavaScript', 'TypeScript', 'Python', 'Java', 'Go', 'Rust'],
        placeholder: 'Add language...',
        required: true
      },
      {
        id: 'frameworks',
        label: 'Frameworks & Libraries',
        type: 'skills',
        icon: '📚',
        defaultSkills: ['React', 'Vue.js', 'Node.js', 'Django', 'Spring Boot', 'Next.js'],
        placeholder: 'Add framework...',
        required: true
      },
      {
        id: 'databases',
        label: 'Databases',
        type: 'skills',
        icon: '🗄️',
        defaultSkills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'Elasticsearch'],
        placeholder: 'Add database...',
        required: false
      },
      {
        id: 'devops',
        label: 'DevOps & Cloud',
        type: 'skills',
        icon: '☁️',
        defaultSkills: ['AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'projects',
        label: 'Key Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'technologies', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // DOCTOR / PHYSICIAN
  // ============================================
  doctor: {
    id: 'doctor',
    name: 'Healthcare / Doctor',
    icon: '🏥',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Board-certified physician with...',
        required: true
      },
      {
        id: 'specialties',
        label: 'Medical Specialties',
        type: 'skills',
        icon: '🩺',
        defaultSkills: ['Internal Medicine', 'Cardiology', 'Pediatrics'],
        placeholder: 'Add specialty...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications & Licenses',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Board Certified', 'State Medical License', 'DEA Certification', 'ACLS', 'BLS'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'residency',
        label: 'Residency & Fellowship',
        type: 'experience',
        icon: '🏥',
        fields: ['institution', 'specialty', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'clinical-experience',
        label: 'Clinical Experience',
        type: 'experience',
        icon: '💉',
        fields: ['hospital', 'position', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Research',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // TEACHER / EDUCATOR
  // ============================================
  teacher: {
    id: 'teacher',
    name: 'Education / Teaching',
    icon: '📚',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Dedicated educator with...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Teaching Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['State Teaching License', 'ESL Certification', 'Special Education'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'subjects',
        label: 'Subjects Taught',
        type: 'skills',
        icon: '📖',
        defaultSkills: ['Mathematics', 'Science', 'History', 'English Literature'],
        placeholder: 'Add subject...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'teaching-experience',
        label: 'Teaching Experience',
        type: 'experience',
        icon: '🏫',
        fields: ['school', 'subject', 'gradeLevel', 'dates', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // LAWYER / ATTORNEY
  // ============================================
  lawyer: {
    id: 'lawyer',
    name: 'Legal / Lawyer',
    icon: '⚖️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Experienced attorney with...',
        required: true
      },
      {
        id: 'bar-admissions',
        label: 'Bar Admissions',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['State Bar of California', 'United States Supreme Court', 'Federal District Court'],
        placeholder: 'Add bar admission...',
        required: true
      },
      {
        id: 'practice-areas',
        label: 'Practice Areas',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Corporate Law', 'Litigation', 'Intellectual Property'],
        placeholder: 'Add practice area...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'case-experience',
        label: 'Notable Cases',
        type: 'projects',
        icon: '📁',
        fields: ['caseName', 'description', 'outcome', 'role'],
        required: false
      }
    ]
  },

  // ============================================
  // PHARMACY
  // ============================================
  pharmacy: {
    id: 'pharmacy',
    name: 'Pharmacy',
    icon: '💊',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Licensed Pharmacist with experience in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['RPh License', 'PharmD', 'BCPS', 'BCOP', 'ACLS'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specialties',
        label: 'Pharmacy Specialties',
        type: 'skills',
        icon: '💊',
        defaultSkills: ['Clinical Pharmacy', 'Oncology', 'Critical Care', 'Pediatrics', 'Geriatrics'],
        placeholder: 'Add specialty...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'residency',
        label: 'Residency & Fellowships',
        type: 'experience',
        icon: '🏥',
        fields: ['institution', 'specialty', 'dates', 'responsibilities'],
        required: false
      },
      {
        id: 'pharmacy-experience',
        label: 'Pharmacy Experience',
        type: 'experience',
        icon: '💼',
        fields: ['employer', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      },
      {
        id: 'skills',
        label: 'Clinical Skills',
        type: 'skills',
        icon: '🩺',
        defaultSkills: ['Medication Therapy Management', 'Immunizations', 'Patient Counseling', 'Drug Interactions', 'IV Compounding'],
        placeholder: 'Add clinical skill...',
        required: false
      }
    ]
  },

  // ============================================
  // NURSING
  // ============================================
  nursing: {
    id: 'nursing',
    name: 'Nursing',
    icon: '👩‍⚕️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Registered Nurse with experience in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['RN License', 'BLS', 'ACLS', 'PALS', 'CNOR', 'CCRN'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specialties',
        label: 'Nursing Specialties',
        type: 'skills',
        icon: '🩺',
        defaultSkills: ['Critical Care', 'Emergency', 'Pediatrics', 'Oncology', 'Labor & Delivery'],
        placeholder: 'Add specialty...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'nursing-experience',
        label: 'Nursing Experience',
        type: 'experience',
        icon: '💼',
        fields: ['facility', 'unit', 'position', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'clinical-skills',
        label: 'Clinical Skills',
        type: 'skills',
        icon: '🩺',
        defaultSkills: ['IV Therapy', 'Wound Care', 'Patient Assessment', 'Medication Administration', 'Charting'],
        placeholder: 'Add clinical skill...',
        required: false
      }
    ]
  },

  // ============================================
  // DENTISTRY
  // ============================================
  dentistry: {
    id: 'dentistry',
    name: 'Dentistry',
    icon: '🦷',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Licensed Dentist with experience in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['DDS/DMD License', 'Board Certified', 'CPR Certified'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specialties',
        label: 'Dental Specialties',
        type: 'skills',
        icon: '🦷',
        defaultSkills: ['Orthodontics', 'Endodontics', 'Periodontics', 'Oral Surgery', 'Pediatric Dentistry'],
        placeholder: 'Add specialty...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'residency',
        label: 'Residency',
        type: 'experience',
        icon: '🏥',
        fields: ['institution', 'specialty', 'dates', 'responsibilities'],
        required: false
      },
      {
        id: 'clinical-experience',
        label: 'Clinical Experience',
        type: 'experience',
        icon: '💼',
        fields: ['practice', 'position', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'procedures',
        label: 'Procedures & Treatments',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Root Canal', 'Crowns', 'Bridges', 'Implants', 'Fillings', 'Extractions'],
        placeholder: 'Add procedure...',
        required: false
      }
    ]
  },

  // ============================================
  // VETERINARY
  // ============================================
  veterinary: {
    id: 'veterinary',
    name: 'Veterinary',
    icon: '🐾',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Licensed Veterinarian with experience in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['DVM License', 'Board Certified', 'ACVS', 'ACVIM'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specialties',
        label: 'Veterinary Specialties',
        type: 'skills',
        icon: '🐾',
        defaultSkills: ['Surgery', 'Internal Medicine', 'Emergency', 'Dentistry', 'Dermatology'],
        placeholder: 'Add specialty...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'clinical-experience',
        label: 'Clinical Experience',
        type: 'experience',
        icon: '💼',
        fields: ['practice', 'position', 'dates', 'species', 'responsibilities'],
        required: true
      },
      {
        id: 'procedures',
        label: 'Procedures & Treatments',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Spay/Neuter', 'Orthopedic Surgery', 'Dental Cleaning', 'Ultrasound', 'Radiology'],
        placeholder: 'Add procedure...',
        required: false
      }
    ]
  },

  // ============================================
  // CLINICAL RESEARCH
  // ============================================
  clinicalResearch: {
    id: 'clinicalResearch',
    name: 'Clinical Research',
    icon: '🔬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Clinical Research Professional with experience in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CCRC', 'CCRP', 'ACRP', 'GCP Certified', 'ICH-GCP'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'therapeutic-areas',
        label: 'Therapeutic Areas',
        type: 'skills',
        icon: '🏥',
        defaultSkills: ['Oncology', 'Cardiology', 'Neurology', 'Infectious Disease', 'Immunology'],
        placeholder: 'Add therapeutic area...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'studies', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'skills',
        label: 'Research Skills',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Protocol Development', 'Data Analysis', 'Regulatory Submissions', 'Site Monitoring', 'Patient Recruitment'],
        placeholder: 'Add research skill...',
        required: false
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // BIOMEDICAL ENGINEERING
  // ============================================
  biomedicalEngineering: {
    id: 'biomedicalEngineering',
    name: 'Biomedical Engineering',
    icon: '🦾',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Biomedical Engineer with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'MATLAB', 'C++', 'Java', 'R'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🦾',
        defaultSkills: ['Biomechanics', 'Medical Imaging', 'Biomaterials', 'Neural Engineering', 'Rehabilitation'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'tools-software',
        label: 'Tools & Software',
        type: 'skills',
        icon: '⚙️',
        defaultSkills: ['SolidWorks', 'AutoCAD', 'Simulink', 'ANSYS', 'Comsol'],
        placeholder: 'Add tool/software...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'projects',
        label: 'Engineering Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['projectName', 'description', 'technologies', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // GENETICS
  // ============================================
  genetics: {
    id: 'genetics',
    name: 'Genetics',
    icon: '🧬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Geneticist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧬',
        defaultSkills: ['Molecular Genetics', 'Genomics', 'Medical Genetics', 'Population Genetics', 'Epigenetics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'techniques',
        label: 'Techniques & Methods',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['PCR', 'Sequencing', 'CRISPR/Cas9', 'Microarray', 'Genotyping', 'Bioinformatics'],
        placeholder: 'Add technique...',
        required: true
      },
      {
        id: 'bioinformatics',
        label: 'Bioinformatics Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['BLAST', 'Python', 'R', 'Bioconductor', 'Galaxy', 'PLINK'],
        placeholder: 'Add bioinformatics tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // NEUROSCIENCE
  // ============================================
  neuroscience: {
    id: 'neuroscience',
    name: 'Neuroscience',
    icon: '🧠',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Neuroscientist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['Cognitive Neuroscience', 'Behavioral Neuroscience', 'Computational Neuroscience', 'Clinical Neuroscience'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'techniques',
        label: 'Research Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['fMRI', 'EEG', 'Patch Clamp', 'Optogenetics', 'Immunohistochemistry', 'Behavioral Assays'],
        placeholder: 'Add technique...',
        required: true
      },
      {
        id: 'programming-tools',
        label: 'Programming & Analysis Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['MATLAB', 'Python', 'R', 'SPSS', 'SPM', 'FSL'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // PSYCHOLOGY
  // ============================================
  psychology: {
    id: 'psychology',
    name: 'Psychology',
    icon: '🧠',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Licensed Psychologist with expertise in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['PhD in Psychology', 'State License', 'Board Certification', 'APA Member'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['Clinical Psychology', 'Cognitive Psychology', 'Developmental Psychology', 'Neuropsychology'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'therapeutic-modalities',
        label: 'Therapeutic Modalities',
        type: 'skills',
        icon: '💬',
        defaultSkills: ['CBT', 'DBT', 'ACT', 'Psychodynamic Therapy', 'Couples Therapy'],
        placeholder: 'Add therapeutic modality...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'clinical-experience',
        label: 'Clinical Experience',
        type: 'experience',
        icon: '💼',
        fields: ['practice', 'position', 'dates', 'population', 'responsibilities'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '🔬',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: false
      }
    ]
  },

  // ============================================
  // SOCIAL WORK
  // ============================================
  socialWork: {
    id: 'socialWork',
    name: 'Social Work',
    icon: '🤝',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Licensed Social Worker with experience in...',
        required: true
      },
      {
        id: 'licenses',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['LCSW', 'LMSW', 'MSW', 'Clinical License'],
        placeholder: 'Add license/certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🤝',
        defaultSkills: ['Child Welfare', 'Mental Health', 'Substance Abuse', 'Family Services', 'Community Organizing'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'social-work-experience',
        label: 'Social Work Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'population', 'responsibilities'],
        required: true
      },
      {
        id: 'skills',
        label: 'Clinical & Intervention Skills',
        type: 'skills',
        icon: '💬',
        defaultSkills: ['Case Management', 'Crisis Intervention', 'Counseling', 'Advocacy', 'Group Therapy'],
        placeholder: 'Add skill...',
        required: false
      }
    ]
  },

  // ============================================
  // ASTRONOMY / ASTROPHYSICS
  // ============================================
  astronomy: {
    id: 'astronomy',
    name: 'Astronomy / Astrophysics',
    icon: '🌌',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Astronomer/Astrophysicist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'C++', 'Fortran', 'IDL', 'MATLAB', 'Julia'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'sub-disciplines',
        label: 'Sub-disciplines',
        type: 'skills',
        icon: '🌌',
        defaultSkills: ['Stellar Astrophysics', 'Galactic Astronomy', 'Cosmology', 'Exoplanets', 'Radio Astronomy'],
        placeholder: 'Add sub-discipline...',
        required: false
      },
      {
        id: 'observational-tools',
        label: 'Observational Tools & Instruments',
        type: 'skills',
        icon: '🔭',
        defaultSkills: ['Telescopes', 'Spectrographs', 'Photometry', 'Interferometry', 'Radio Arrays'],
        placeholder: 'Add tool/instrument...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Analysis Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['IRAF', 'CASA', 'DS9', 'HEASOFT', 'GALFIT', 'ASTROPY'],
        placeholder: 'Add software tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Observing Proposals',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'arxivId'],
        required: false
      },
      {
        id: 'observing-experience',
        label: 'Observing Experience',
        type: 'skills',
        icon: '🔭',
        defaultSkills: ['Optical Observing', 'Radio Observing', 'Space Telescopes', 'Ground-based Telescopes'],
        placeholder: 'Add observing experience...',
        required: false
      }
    ]
  },

  // ============================================
  // MATERIALS SCIENCE
  // ============================================
  materialsScience: {
    id: 'materialsScience',
    name: 'Materials Science',
    icon: '🧪',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Materials Scientist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'MATLAB', 'R', 'C++', 'Julia', 'Fortran'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'material-types',
        label: 'Material Types',
        type: 'skills',
        icon: '🧪',
        defaultSkills: ['Metals', 'Ceramics', 'Polymers', 'Composites', 'Semiconductors', 'Biomaterials', 'Nanomaterials'],
        placeholder: 'Add material type...',
        required: true
      },
      {
        id: 'characterization',
        label: 'Characterization Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['SEM', 'TEM', 'XRD', 'XPS', 'AFM', 'DSC', 'TGA', 'Raman Spectroscopy'],
        placeholder: 'Add characterization technique...',
        required: true
      },
      {
        id: 'simulation-tools',
        label: 'Simulation & Modeling Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['DFT', 'Molecular Dynamics', 'COMSOL', 'ANSYS', 'LAMMPS', 'VASP', 'Quantum ESPRESSO'],
        placeholder: 'Add simulation tool...',
        required: false
      },
      {
        id: 'processing-methods',
        label: 'Processing & Manufacturing Methods',
        type: 'skills',
        icon: '🏭',
        defaultSkills: ['CVD', 'PVD', 'Sputtering', 'Sol-Gel', '3D Printing', 'Heat Treatment', 'Electroplating'],
        placeholder: 'Add processing method...',
        required: false
      },
      {
        id: 'applications',
        label: 'Applications & Industries',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Aerospace', 'Automotive', 'Electronics', 'Energy', 'Biomedical', 'Construction'],
        placeholder: 'Add application...',
        required: false
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Patents',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'patentNumber'],
        required: false
      }
    ]
  },

  // ============================================
  // FOOD SCIENCE
  // ============================================
  foodScience: {
    id: 'foodScience',
    name: 'Food Science',
    icon: '🍎',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Food Scientist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🍎',
        defaultSkills: ['Food Chemistry', 'Food Microbiology', 'Food Engineering', 'Nutrition', 'Sensory Science'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'food-safety',
        label: 'Food Safety & Quality',
        type: 'skills',
        icon: '🛡️',
        defaultSkills: ['HACCP', 'GMP', 'FSSC 22000', 'ISO 22000', 'Food Safety Auditing', 'Microbial Testing'],
        placeholder: 'Add food safety skill...',
        required: true
      },
      {
        id: 'techniques',
        label: 'Analytical Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['HPLC', 'GC-MS', 'Spectrophotometry', 'Rheology', 'Texture Analysis', 'Microscopy'],
        placeholder: 'Add analytical technique...',
        required: true
      },
      {
        id: 'product-development',
        label: 'Product Development',
        type: 'skills',
        icon: '🧪',
        defaultSkills: ['Formulation', 'Sensory Testing', 'Shelf Life Study', 'Process Optimization', 'Recipe Development'],
        placeholder: 'Add product development skill...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['FDA Regulations', 'USDA', 'EU Food Law', 'CODEX', 'Labeling Compliance'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'food-industry-experience',
        label: 'Food Industry Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // NUTRITION SCIENCE
  // ============================================
  nutritionScience: {
    id: 'nutritionScience',
    name: 'Nutrition Science',
    icon: '🥗',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Nutrition Scientist with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications & Licenses',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['RD/RDN', 'CNS', 'CDN', 'LDN', 'Nutrition Certification'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🥗',
        defaultSkills: ['Clinical Nutrition', 'Sports Nutrition', 'Pediatric Nutrition', 'Geriatric Nutrition', 'Public Health'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'clinical-skills',
        label: 'Clinical Skills',
        type: 'skills',
        icon: '🩺',
        defaultSkills: ['Nutrition Assessment', 'Medical Nutrition Therapy', 'Diet Planning', 'Patient Counseling', 'Anthropometrics'],
        placeholder: 'Add clinical skill...',
        required: false
      },
      {
        id: 'research-methods',
        label: 'Research Methods',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Dietary Assessment', 'Biochemical Analysis', 'Epidemiology', 'Clinical Trials', 'Statistical Analysis'],
        placeholder: 'Add research method...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'internship',
        label: 'Dietetic Internship',
        type: 'experience',
        icon: '🏥',
        fields: ['institution', 'rotation', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'clinical-experience',
        label: 'Clinical Experience',
        type: 'experience',
        icon: '💼',
        fields: ['facility', 'position', 'dates', 'population', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // SPORTS SCIENCE
  // ============================================
  sportsScience: {
    id: 'sportsScience',
    name: 'Sports Science',
    icon: '🏃',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Sports Scientist with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CSCS', 'CPT', 'NSCA', 'ACSM', 'ESSA'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🏃',
        defaultSkills: ['Biomechanics', 'Exercise Physiology', 'Motor Control', 'Rehabilitation', 'Performance Nutrition'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'assessment-tools',
        label: 'Assessment Tools',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['VO2 Max Testing', 'Biomechanical Analysis', 'Force Plates', 'Motion Capture', 'GPS Tracking'],
        placeholder: 'Add assessment tool...',
        required: true
      },
      {
        id: 'analysis-software',
        label: 'Analysis Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['SPSS', 'R', 'MATLAB', 'Simi Motion', 'Qualisys', 'Video Analysis'],
        placeholder: 'Add software...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'sports-experience',
        label: 'Sports Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'sport', 'position', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '🔬',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: false
      }
    ]
  },

  // ============================================
  // GEOLOGY / GEOLOGIST
  // ============================================
  geology: {
    id: 'geology',
    name: 'Geology / Geologist',
    icon: '🗻',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Geologist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🗻',
        defaultSkills: ['Structural Geology', 'Sedimentology', 'Mineralogy', 'Paleontology', 'Geochemistry', 'Geophysics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'field-techniques',
        label: 'Field Techniques',
        type: 'skills',
        icon: '🔭',
        defaultSkills: ['Mapping', 'Core Sampling', 'Stratigraphy', 'Fossil Collection', 'GPS Surveying'],
        placeholder: 'Add field technique...',
        required: true
      },
      {
        id: 'lab-techniques',
        label: 'Laboratory Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Petrography', 'XRF', 'ICP-MS', 'SEM', 'Thin Section Analysis', 'XRD'],
        placeholder: 'Add lab technique...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & GIS Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['ArcGIS', 'QGIS', 'Global Mapper', 'Gemcom', 'Petrel', 'Surfer'],
        placeholder: 'Add software tool...',
        required: false
      },
      {
        id: 'industry-applications',
        label: 'Industry Applications',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Mineral Exploration', 'Oil & Gas', 'Environmental Consulting', 'Geotechnical', 'Hydrogeology'],
        placeholder: 'Add industry application...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'field-experience',
        label: 'Field Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'project', 'location', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Reports',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // BIOTECHNOLOGY
  // ============================================
  biotechnology: {
    id: 'biotechnology',
    name: 'Biotechnology',
    icon: '🧬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Biotechnology Professional with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧬',
        defaultSkills: ['Molecular Biology', 'Cell Culture', 'Fermentation', 'Gene Editing', 'Protein Engineering'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'lab-techniques',
        label: 'Laboratory Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['PCR', 'Western Blot', 'ELISA', 'Flow Cytometry', 'Microscopy', 'Spectrophotometry'],
        placeholder: 'Add lab technique...',
        required: true
      },
      {
        id: 'bioprocessing',
        label: 'Bioprocessing & Manufacturing',
        type: 'skills',
        icon: '🏭',
        defaultSkills: ['Upstream Processing', 'Downstream Processing', 'Bioreactor Operation', 'Purification', 'QA/QC'],
        placeholder: 'Add bioprocessing skill...',
        required: false
      },
      {
        id: 'bioinformatics',
        label: 'Bioinformatics Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['BLAST', 'Python', 'R', 'Bioconductor', 'Genome Browsers', 'Sequence Analysis'],
        placeholder: 'Add bioinformatics tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['FDA Regulations', 'ICH Guidelines', 'GMP', 'GLP', 'Clinical Trials'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Patents',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'patentNumber'],
        required: false
      }
    ]
  },

  // ============================================
  // ENVIRONMENTAL SCIENCE
  // ============================================
  environmentalScience: {
    id: 'environmentalScience',
    name: 'Environmental Science',
    icon: '🌍',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Environmental Scientist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🌍',
        defaultSkills: ['Ecology', 'Conservation', 'Environmental Chemistry', 'Environmental Policy', 'Climate Science'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'field-techniques',
        label: 'Field & Monitoring Techniques',
        type: 'skills',
        icon: '🔭',
        defaultSkills: ['Water Sampling', 'Soil Analysis', 'Air Quality Monitoring', 'Biodiversity Surveys', 'GIS Mapping'],
        placeholder: 'Add field technique...',
        required: true
      },
      {
        id: 'lab-techniques',
        label: 'Laboratory Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Spectrophotometry', 'Chromatography', 'Titration', 'Microbiology', 'Toxicology Testing'],
        placeholder: 'Add lab technique...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Modeling Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['ArcGIS', 'QGIS', 'R', 'Python', 'HEC-RAS', 'MODFLOW', 'Climate Models'],
        placeholder: 'Add software tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['EPA Regulations', 'Clean Water Act', 'Clean Air Act', 'NEPA', 'Environmental Impact Assessment'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'field-experience',
        label: 'Field Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'project', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // PHYSICS
  // ============================================
  physics: {
    id: 'physics',
    name: 'Physics',
    icon: '⚛️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Physicist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'C++', 'Fortran', 'MATLAB', 'Julia', 'R'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'sub-disciplines',
        label: 'Sub-disciplines',
        type: 'skills',
        icon: '⚛️',
        defaultSkills: ['Condensed Matter Physics', 'High Energy Physics', 'Astrophysics', 'Quantum Mechanics', 'Thermodynamics'],
        placeholder: 'Add sub-discipline...',
        required: false
      },
      {
        id: 'experimental-techniques',
        label: 'Experimental Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Spectroscopy', 'Cryogenics', 'Vacuum Systems', 'Laser Physics', 'Detectors', 'Accelerators'],
        placeholder: 'Add experimental technique...',
        required: true
      },
      {
        id: 'simulation-tools',
        label: 'Simulation & Modeling Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Monte Carlo', 'Geant4', 'OpenFOAM', 'ROOT', 'Mathematica', 'COMSOL'],
        placeholder: 'Add simulation tool...',
        required: false
      },
      {
        id: 'data-analysis',
        label: 'Data Analysis Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Statistical Analysis', 'Machine Learning', 'Signal Processing', 'Bayesian Analysis', 'Error Analysis'],
        placeholder: 'Add data analysis skill...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'arxivId'],
        required: false
      }
    ]
  },

  // ============================================
  // CHEMISTRY
  // ============================================
  chemistry: {
    id: 'chemistry',
    name: 'Chemistry',
    icon: '🧫',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Chemist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'MATLAB', 'R', 'C++', 'Julia', 'SQL'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧫',
        defaultSkills: ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Analytical Chemistry', 'Biochemistry'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'analytical-techniques',
        label: 'Analytical Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['HPLC', 'GC-MS', 'LC-MS', 'NMR', 'FTIR', 'UV-Vis', 'ICP-MS', 'Titration'],
        placeholder: 'Add analytical technique...',
        required: true
      },
      {
        id: 'synthesis-methods',
        label: 'Synthesis Methods',
        type: 'skills',
        icon: '⚗️',
        defaultSkills: ['Organic Synthesis', 'Inorganic Synthesis', 'Polymerization', 'Crystallization', 'Distillation'],
        placeholder: 'Add synthesis method...',
        required: false
      },
      {
        id: 'computational-tools',
        label: 'Computational Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Gaussian', 'DFT', 'Molecular Dynamics', 'ChemDraw', 'PyMOL', 'AutoDock'],
        placeholder: 'Add computational tool...',
        required: false
      },
      {
        id: 'safety',
        label: 'Safety & Regulatory',
        type: 'skills',
        icon: '🛡️',
        defaultSkills: ['Chemical Safety', 'GHS', 'Hazard Communication', 'Lab Safety', 'EPA Regulations'],
        placeholder: 'Add safety skill...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications & Patents',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi', 'patentNumber'],
        required: false
      }
    ]
  },

  // ============================================
  // BIOLOGY
  // ============================================
  biology: {
    id: 'biology',
    name: 'Biology',
    icon: '🧬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Biologist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'R', 'SQL', 'Java', 'MATLAB'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧬',
        defaultSkills: ['Cell Biology', 'Molecular Biology', 'Microbiology', 'Ecology', 'Evolutionary Biology', 'Genetics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'lab-techniques',
        label: 'Laboratory Techniques',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['PCR', 'Gel Electrophoresis', 'Western Blot', 'Immunohistochemistry', 'Cell Culture', 'Microscopy'],
        placeholder: 'Add lab technique...',
        required: true
      },
      {
        id: 'bioinformatics',
        label: 'Bioinformatics & Data Analysis',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Sequence Analysis', 'Genome Browsers', 'Phylogenetics', 'BLAST', 'Bioconductor', 'Gene Ontology'],
        placeholder: 'Add bioinformatics tool...',
        required: false
      },
      {
        id: 'field-experience',
        label: 'Field Experience',
        type: 'skills',
        icon: '🌿',
        defaultSkills: ['Sampling', 'Taxonomy', 'Ecological Surveys', 'Data Collection', 'Species Identification'],
        placeholder: 'Add field experience...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // FINANCE / BANKING
  // ============================================
  finance: {
    id: 'finance',
    name: 'Finance / Banking',
    icon: '💰',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Finance professional with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '💰',
        defaultSkills: ['Corporate Finance', 'Commercial Banking', 'Investment Banking', 'Financial Planning', 'Risk Management'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'financial-skills',
        label: 'Financial Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Financial Modeling', 'Valuation', 'DCF Analysis', 'Financial Statement Analysis', 'Capital Budgeting'],
        placeholder: 'Add financial skill...',
        required: true
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Basel III', 'Dodd-Frank', 'SEC Regulations', 'GDPR', 'AML/KYC'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Excel', 'Bloomberg Terminal', 'Reuters', 'SAP', 'Oracle Financials', 'QuickBooks'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CFA', 'FRM', 'CPA', 'Series 7', 'Series 63'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // ECONOMICS
  // ============================================
  economics: {
    id: 'economics',
    name: 'Economics / Economist',
    icon: '📈',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Economist with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '📈',
        defaultSkills: ['Macroeconomics', 'Microeconomics', 'Behavioral Economics', 'Development Economics', 'International Economics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'quantitative-skills',
        label: 'Quantitative Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Econometrics', 'Statistical Analysis', 'Forecasting', 'Data Modeling', 'Regression Analysis'],
        placeholder: 'Add quantitative skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Stata', 'R', 'Python', 'MATLAB', 'EViews', 'SPSS', 'SAS'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'policy-areas',
        label: 'Policy Areas',
        type: 'skills',
        icon: '🏛️',
        defaultSkills: ['Monetary Policy', 'Fiscal Policy', 'Labor Economics', 'Healthcare Economics', 'Environmental Economics'],
        placeholder: 'Add policy area...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // ACCOUNTING / CA
  // ============================================
  accounting: {
    id: 'accounting',
    name: 'Accounting / CA',
    icon: '📊',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Chartered Accountant with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications & Licenses',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CA', 'CPA', 'ACCA', 'CMA', 'Enrolled Agent'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'accounting-skills',
        label: 'Accounting Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Auditing', 'Taxation', 'Financial Reporting', 'Bookkeeping', 'Payroll Management'],
        placeholder: 'Add accounting skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['QuickBooks', 'SAP', 'Oracle', 'Tally', 'Xero', 'MS Excel', 'Advance Excel'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['GAAP', 'IFRS', 'Tax Laws', 'Companies Act', 'GST'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'articleship',
        label: 'Articleship / Internship',
        type: 'experience',
        icon: '💼',
        fields: ['firm', 'position', 'dates', 'responsibilities', 'clientsHandled'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // INVESTMENT BANKING
  // ============================================
  investmentBanking: {
    id: 'investmentBanking',
    name: 'Investment Banking',
    icon: '🏦',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Investment Banking professional with expertise in...',
        required: true
      },
      {
        id: 'ib-skills',
        label: 'Investment Banking Skills',
        type: 'skills',
        icon: '🏦',
        defaultSkills: ['M&A Advisory', 'Leveraged Buyouts', 'Corporate Restructuring', 'Capital Raising', 'IPO Advisory'],
        placeholder: 'Add IB skill...',
        required: true
      },
      {
        id: 'financial-modeling',
        label: 'Financial Modeling',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['DCF Modeling', 'LBO Modeling', 'Merger Modeling', 'Valuation Analysis', 'Scenario Analysis'],
        placeholder: 'Add modeling skill...',
        required: true
      },
      {
        id: 'deal-experience',
        label: 'Deal Experience',
        type: 'projects',
        icon: '🤝',
        fields: ['dealName', 'dealType', 'value', 'role', 'outcome'],
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Excel', 'Bloomberg', 'Capital IQ', 'FactSet', 'Thomson Reuters', 'VBA'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CFA', 'Series 79', 'Series 63', 'CPA'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors', 'gpa'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'dealHighlights', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // WEALTH MANAGEMENT
  // ============================================
  wealthManagement: {
    id: 'wealthManagement',
    name: 'Wealth Management',
    icon: '💎',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Wealth Management professional with expertise in...',
        required: true
      },
      {
        id: 'wm-skills',
        label: 'Wealth Management Skills',
        type: 'skills',
        icon: '💎',
        defaultSkills: ['Portfolio Management', 'Asset Allocation', 'Investment Strategy', 'Estate Planning', 'Tax Strategy'],
        placeholder: 'Add wealth management skill...',
        required: true
      },
      {
        id: 'client-types',
        label: 'Client Types',
        type: 'skills',
        icon: '👔',
        defaultSkills: ['High Net Worth', 'Ultra High Net Worth', 'Family Offices', 'Institutional Clients'],
        placeholder: 'Add client type...',
        required: false
      },
      {
        id: 'products-services',
        label: 'Products & Services',
        type: 'skills',
        icon: '📦',
        defaultSkills: ['Mutual Funds', 'ETFs', 'Hedge Funds', 'Private Equity', 'Structured Products', 'Insurance'],
        placeholder: 'Add product/service...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Portfolio Management Systems', 'Risk Analytics', 'CRMs', 'Bloomberg', 'Morningstar'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CFA', 'CFP', 'CIMA', 'Series 65', 'Series 7'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'aumManaged', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // FINTECH
  // ============================================
  fintech: {
    id: 'fintech',
    name: 'FinTech',
    icon: '📱',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'FinTech professional with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'Java', 'JavaScript', 'SQL', 'C++', 'Rust'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'fintech-areas',
        label: 'FinTech Areas',
        type: 'skills',
        icon: '📱',
        defaultSkills: ['Digital Banking', 'Payments', 'Blockchain', 'Lending', 'InsurTech', 'RegTech'],
        placeholder: 'Add FinTech area...',
        required: false
      },
      {
        id: 'financial-skills',
        label: 'Financial Skills',
        type: 'skills',
        icon: '💰',
        defaultSkills: ['Risk Management', 'Compliance', 'Financial Modeling', 'Algorithmic Trading'],
        placeholder: 'Add financial skill...',
        required: false
      },
      {
        id: 'frameworks-tools',
        label: 'Frameworks & Tools',
        type: 'skills',
        icon: '⚙️',
        defaultSkills: ['React', 'Node.js', 'Django', 'Spring Boot', 'AWS', 'Docker', 'Kafka'],
        placeholder: 'Add framework/tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['PCI-DSS', 'GDPR', 'PSD2', 'AML/KYC', 'Data Privacy'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'technologiesUsed'],
        required: true
      }
    ]
  },

  // ============================================
  // RISK MANAGEMENT
  // ============================================
  riskManagement: {
    id: 'riskManagement',
    name: 'Risk Management',
    icon: '⚠️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Risk Management professional with expertise in...',
        required: true
      },
      {
        id: 'risk-types',
        label: 'Risk Types',
        type: 'skills',
        icon: '⚠️',
        defaultSkills: ['Credit Risk', 'Market Risk', 'Operational Risk', 'Liquidity Risk', 'Regulatory Risk'],
        placeholder: 'Add risk type...',
        required: true
      },
      {
        id: 'risk-skills',
        label: 'Risk Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Risk Assessment', 'Stress Testing', 'VaR Modeling', 'Scenario Analysis', 'Risk Reporting'],
        placeholder: 'Add risk skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['RiskMetrics', 'Bloomberg', 'SQL', 'Python', 'R', 'Excel', 'Tableau'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Frameworks',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Basel III', 'Solvency II', 'Dodd-Frank', 'IFRS 9', 'CECL'],
        placeholder: 'Add regulatory framework...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['FRM', 'PRM', 'CFA', 'ERP'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'riskMetricsManaged'],
        required: true
      }
    ]
  },

  // ============================================
  // INSURANCE
  // ============================================
  insurance: {
    id: 'insurance',
    name: 'Insurance',
    icon: '🛡️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Insurance professional with expertise in...',
        required: true
      },
      {
        id: 'insurance-types',
        label: 'Insurance Types',
        type: 'skills',
        icon: '🛡️',
        defaultSkills: ['Life Insurance', 'Health Insurance', 'Property & Casualty', 'Auto Insurance', 'Reinsurance'],
        placeholder: 'Add insurance type...',
        required: false
      },
      {
        id: 'insurance-skills',
        label: 'Insurance Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Underwriting', 'Claims Management', 'Actuarial Science', 'Risk Assessment', 'Policy Management'],
        placeholder: 'Add insurance skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Insurance CRMs', 'Claims Management Systems', 'Actuarial Software', 'Excel'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Insurance Regulations', 'Compliance', 'NAIC', 'Solvency II', 'IRDA'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['AINS', 'CPCU', 'FLMI', 'ACS', 'CISR'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'portfolioManaged'],
        required: true
      }
    ]
  },

  // ============================================
  // HUMAN RESOURCES
  // ============================================
  humanResources: {
    id: 'humanResources',
    name: 'Human Resources',
    icon: '👥',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Human Resources professional with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['SHRM-CP', 'SHRM-SCP', 'PHR', 'SPHR', 'HRCI', 'CIPD'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'hr-specializations',
        label: 'HR Specializations',
        type: 'skills',
        icon: '👥',
        defaultSkills: ['Talent Acquisition', 'Employee Relations', 'Compensation & Benefits', 'HR Analytics', 'Learning & Development'],
        placeholder: 'Add HR specialization...',
        required: false
      },
      {
        id: 'hr-skills',
        label: 'HR Skills',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Recruitment', 'Performance Management', 'Onboarding', 'Offboarding', 'Employee Engagement', 'Policy Development'],
        placeholder: 'Add HR skill...',
        required: true
      },
      {
        id: 'hr-software',
        label: 'HR Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Workday', 'SAP SuccessFactors', 'ADP', 'BambooHR', 'Greenhouse', 'Lever', 'HRIS'],
        placeholder: 'Add HR software...',
        required: false
      },
      {
        id: 'legal-compliance',
        label: 'Legal & Compliance',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['Labor Laws', 'EEO Compliance', 'FMLA', 'ADA', 'OSHA', 'Data Privacy'],
        placeholder: 'Add legal/compliance knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // REAL ESTATE MANAGEMENT
  // ============================================
  realEstateManagement: {
    id: 'realEstateManagement',
    name: 'Real Estate Management',
    icon: '🏠',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Real Estate professional with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🏠',
        defaultSkills: ['Residential', 'Commercial', 'Industrial', 'Retail', 'Mixed-Use Developments'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'real-estate-skills',
        label: 'Real Estate Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Property Valuation', 'Lease Management', 'Tenant Relations', 'Market Analysis', 'Due Diligence'],
        placeholder: 'Add real estate skill...',
        required: true
      },
      {
        id: 'financial-skills',
        label: 'Financial Skills',
        type: 'skills',
        icon: '💰',
        defaultSkills: ['Financial Analysis', 'Investment Analysis', 'ROI Calculations', 'Capital Budgeting', 'Portfolio Management'],
        placeholder: 'Add financial skill...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Excel', 'ARCGIS', 'Real Estate CRMs', 'Property Management Software', 'Yardi', 'AppFolio'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'legal-knowledge',
        label: 'Legal & Regulatory Knowledge',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['Zoning Laws', 'Building Codes', 'Fair Housing Act', 'Environmental Regulations', 'Contract Law'],
        placeholder: 'Add legal knowledge...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Real Estate License', 'CCIM', 'SIOR', 'CPM', 'CAM', 'CRS'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'portfolioSize', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // PROJECT MANAGEMENT
  // ============================================
  projectManagement: {
    id: 'projectManagement',
    name: 'Project Management',
    icon: '📊',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Project Manager with expertise in...',
        required: true
      },
      {
        id: 'project-methodology',
        label: 'Project Management Methodologies',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Agile', 'Scrum', 'Kanban', 'Waterfall', 'PMI/PMBOK', 'SAFe', 'Lean', 'Six Sigma'],
        placeholder: 'Add methodology...',
        required: true
      },
      {
        id: 'project-skills',
        label: 'Project Management Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Project Planning', 'Budgeting', 'Risk Management', 'Stakeholder Communication', 'Resource Allocation'],
        placeholder: 'Add project skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Project Management Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['JIRA', 'Asana', 'Trello', 'Microsoft Project', 'Monday.com', 'Basecamp', 'ClickUp'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['PMP', 'Certified ScrumMaster', 'PRINCE2', 'PMI-ACP', 'CAPM'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'industry-areas',
        label: 'Industry Areas',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['IT', 'Construction', 'Healthcare', 'Finance', 'Manufacturing', 'Marketing'],
        placeholder: 'Add industry area...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'projectHighlights', 'budgetManaged', 'teamSize', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // BEHAVIORAL ECONOMICS
  // ============================================
  behavioralEconomics: {
    id: 'behavioralEconomics',
    name: 'Behavioral Economics',
    icon: '🧠',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Behavioral Economist with expertise in...',
        required: true
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'R', 'Stata', 'SQL', 'MATLAB', 'Julia'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🧠',
        defaultSkills: ['Decision Making', 'Cognitive Biases', 'Social Preferences', 'Experimental Economics', 'Neuroeconomics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'research-methods',
        label: 'Research Methods',
        type: 'skills',
        icon: '🔬',
        defaultSkills: ['Experimental Design', 'A/B Testing', 'Survey Design', 'Regression Analysis', 'Causal Inference'],
        placeholder: 'Add research method...',
        required: true
      },
      {
        id: 'application-areas',
        label: 'Application Areas',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Policy Design', 'Public Health', 'Marketing', 'Financial Decision Making', 'Nudge Design'],
        placeholder: 'Add application area...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Python', 'R', 'Stata', 'SPSS', 'Qualtrics', 'Mplus'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'research-experience',
        label: 'Research Experience',
        type: 'experience',
        icon: '💼',
        fields: ['institution', 'position', 'dates', 'researchFocus', 'responsibilities'],
        required: true
      },
      {
        id: 'publications',
        label: 'Publications',
        type: 'publications',
        icon: '📄',
        fields: ['title', 'journal', 'year', 'doi'],
        required: false
      }
    ]
  },

  // ============================================
  // BUSINESS ANALYSIS
  // ============================================
  businessAnalysis: {
    id: 'businessAnalysis',
    name: 'Business Analyst',
    icon: '📉',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Business Analyst with expertise in...',
        required: true
      },
      {
        id: 'ba-skills',
        label: 'Business Analysis Skills',
        type: 'skills',
        icon: '📉',
        defaultSkills: ['Requirement Gathering', 'Data Analysis', 'Process Mapping', 'Stakeholder Management', 'Solution Design'],
        placeholder: 'Add BA skill...',
        required: true
      },
      {
        id: 'techniques',
        label: 'Business Analysis Techniques',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['SWOT Analysis', 'Cost-Benefit Analysis', 'Root Cause Analysis', 'Gap Analysis', 'Financial Modeling'],
        placeholder: 'Add technique...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Excel', 'SQL', 'Tableau', 'Power BI', 'JIRA', 'Confluence', 'Visio', 'Balsamiq'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'methodologies',
        label: 'Methodologies',
        type: 'skills',
        icon: '🔄',
        defaultSkills: ['Agile', 'Waterfall', 'Scrum', 'Lean', 'Six Sigma'],
        placeholder: 'Add methodology...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CBAP', 'CCBA', 'ECBA', 'PMI-PBA', 'Six Sigma'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'deliverables', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // OPERATIONS MANAGEMENT
  // ============================================
  operationsManagement: {
    id: 'operationsManagement',
    name: 'Operations Management',
    icon: '⚙️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Operations Manager with expertise in...',
        required: true
      },
      {
        id: 'operations-skills',
        label: 'Operations Skills',
        type: 'skills',
        icon: '⚙️',
        defaultSkills: ['Process Improvement', 'Supply Chain Management', 'Inventory Control', 'Quality Assurance', 'Logistics'],
        placeholder: 'Add operations skill...',
        required: true
      },
      {
        id: 'methodologies',
        label: 'Methodologies',
        type: 'skills',
        icon: '🔄',
        defaultSkills: ['Lean Management', 'Six Sigma', 'Kaizen', '5S', 'JIT', 'Total Quality Management'],
        placeholder: 'Add methodology...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['ERP Systems', 'SAP', 'Oracle', 'Microsoft Dynamics', 'Tableau', 'Power BI'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Six Sigma Black Belt', 'Lean Certification', 'APICS CPIM', 'CSCP', 'ISO 9001'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'operationalMetrics', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // QUALITY MANAGEMENT
  // ============================================
  qualityManagement: {
    id: 'qualityManagement',
    name: 'Quality Management',
    icon: '✅',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Quality Management professional with expertise in...',
        required: true
      },
      {
        id: 'quality-skills',
        label: 'Quality Management Skills',
        type: 'skills',
        icon: '✅',
        defaultSkills: ['Quality Assurance', 'Quality Control', 'Auditing', 'Continuous Improvement', 'Root Cause Analysis'],
        placeholder: 'Add quality skill...',
        required: true
      },
      {
        id: 'methodologies',
        label: 'Methodologies',
        type: 'skills',
        icon: '🔄',
        defaultSkills: ['Six Sigma', 'Lean', 'Kaizen', 'PDCA', '8D Problem Solving', 'FMEA', 'SPC'],
        placeholder: 'Add methodology...',
        required: false
      },
      {
        id: 'standards',
        label: 'Quality Standards',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['ISO 9001', 'ISO 14001', 'ISO 45001', 'IATF 16949', 'AS9100', 'GMP'],
        placeholder: 'Add quality standard...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['SAP QM', 'SPC Software', 'Minitab', 'Excel', 'Quality CRMs'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['ASQ Certified', 'Six Sigma Black Belt', 'CQA', 'CQE', 'CMQ/OE'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'qualityMetrics', 'improvementProjects'],
        required: true
      }
    ]
  },

  // ============================================
  // FACILITIES MANAGEMENT
  // ============================================
  facilitiesManagement: {
    id: 'facilitiesManagement',
    name: 'Facilities Management',
    icon: '🏢',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Facilities Manager with expertise in...',
        required: true
      },
      {
        id: 'facilities-skills',
        label: 'Facilities Management Skills',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Building Maintenance', 'Space Planning', 'Vendor Management', 'Budget Management', 'Health & Safety'],
        placeholder: 'Add facilities skill...',
        required: true
      },
      {
        id: 'technical-skills',
        label: 'Technical Skills',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['HVAC Systems', 'Electrical Systems', 'Plumbing', 'Building Automation', 'Fire Safety Systems'],
        placeholder: 'Add technical skill...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['CAFM Systems', 'CMMS', 'Facilities Management Software', 'AutoCAD', 'Building Information Modeling'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['FMP', 'CFM', 'SFP', 'REH', 'BOMA Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['OSHA Compliance', 'Building Codes', 'Fire Safety Regulations', 'Environmental Regulations'],
        placeholder: 'Add regulatory knowledge...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'facilitySize', 'teamSize', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // COMPLIANCE MANAGEMENT
  // ============================================
  complianceManagement: {
    id: 'complianceManagement',
    name: 'Compliance Management',
    icon: '📋',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Compliance professional with expertise in...',
        required: true
      },
      {
        id: 'compliance-areas',
        label: 'Compliance Areas',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Regulatory Compliance', 'Anti-Money Laundering', 'Data Privacy', 'Ethics', 'Corporate Compliance'],
        placeholder: 'Add compliance area...',
        required: true
      },
      {
        id: 'regulatory-knowledge',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['GDPR', 'HIPAA', 'SOX', 'PCI-DSS', 'FCPA', 'Basel III', 'Dodd-Frank'],
        placeholder: 'Add regulatory knowledge...',
        required: true
      },
      {
        id: 'compliance-skills',
        label: 'Compliance Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Policy Development', 'Risk Assessment', 'Auditing', 'Investigation', 'Training & Education'],
        placeholder: 'Add compliance skill...',
        required: false
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Compliance Management Systems', 'GRC Software', 'Audit Tools', 'Risk Management Software'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CAMS', 'CRCM', 'CCEP', 'CISA', 'CPA'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'compliancePrograms', 'regulatoryInteractions'],
        required: true
      }
    ]
  },

  // ============================================
  // CORPORATE GOVERNANCE
  // ============================================
  corporateGovernance: {
    id: 'corporateGovernance',
    name: 'Corporate Governance',
    icon: '🏛️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Corporate Governance professional with expertise in...',
        required: true
      },
      {
        id: 'governance-skills',
        label: 'Corporate Governance Skills',
        type: 'skills',
        icon: '🏛️',
        defaultSkills: ['Board Governance', 'Shareholder Relations', 'Executive Compensation', 'Risk Oversight', 'Compliance'],
        placeholder: 'Add governance skill...',
        required: true
      },
      {
        id: 'regulatory-knowledge',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['Corporate Law', 'SEC Regulations', 'SOX', 'NYSE/NASDAQ Rules', 'ESG Frameworks'],
        placeholder: 'Add regulatory knowledge...',
        required: true
      },
      {
        id: 'governance-areas',
        label: 'Governance Areas',
        type: 'skills',
        icon: '🏛️',
        defaultSkills: ['Board Committees', 'Audit Committee', 'Nominating Committee', 'Compensation Committee', 'ESG Reporting'],
        placeholder: 'Add governance area...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['ACG Certified', 'Board Certified', 'ICP', 'ESG Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'boardInteractions', 'governanceInitiatives'],
        required: true
      }
    ]
  },

  // ============================================
  // BOARD GOVERNANCE
  // ============================================
  boardGovernance: {
    id: 'boardGovernance',
    name: 'Board Governance',
    icon: '👔',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Board Governance professional with expertise in...',
        required: true
      },
      {
        id: 'board-skills',
        label: 'Board Governance Skills',
        type: 'skills',
        icon: '👔',
        defaultSkills: ['Board Strategy', 'Directorship', 'Committee Leadership', 'Stakeholder Engagement', 'Risk Management'],
        placeholder: 'Add board skill...',
        required: true
      },
      {
        id: 'board-roles',
        label: 'Board Roles & Experience',
        type: 'skills',
        icon: '👔',
        defaultSkills: ['Independent Director', 'Chairperson', 'Committee Chair', 'Board Member', 'Board Advisor'],
        placeholder: 'Add board role...',
        required: false
      },
      {
        id: 'industry-expertise',
        label: 'Industry Expertise',
        type: 'skills',
        icon: '🏢',
        defaultSkills: ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Energy', 'Retail'],
        placeholder: 'Add industry expertise...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Board Leadership', 'Corporate Governance Certification', 'ESG Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'board-experience',
        label: 'Board Experience',
        type: 'experience',
        icon: '💼',
        fields: ['boardName', 'position', 'dates', 'boardSize', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // EVENT MANAGEMENT
  // ============================================
  eventManagement: {
    id: 'eventManagement',
    name: 'Event Management',
    icon: '🎪',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Event Management professional with expertise in...',
        required: true
      },
      {
        id: 'event-types',
        label: 'Event Types',
        type: 'skills',
        icon: '🎪',
        defaultSkills: ['Corporate Events', 'Weddings', 'Conferences', 'Trade Shows', 'Festivals', 'Private Parties'],
        placeholder: 'Add event type...',
        required: false
      },
      {
        id: 'event-skills',
        label: 'Event Management Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Event Planning', 'Vendor Management', 'Budget Management', 'Venue Sourcing', 'Logistics'],
        placeholder: 'Add event skill...',
        required: true
      },
      {
        id: 'software-tools',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Eventbrite', 'Cvent', 'Hopin', 'Zoom Events', 'Airbnb Experiences', 'Project Management Tools'],
        placeholder: 'Add software/tool...',
        required: false
      },
      {
        id: 'marketing-skills',
        label: 'Marketing & Promotion',
        type: 'skills',
        icon: '📢',
        defaultSkills: ['Social Media Marketing', 'Email Campaigns', 'Branding', 'Sponsorship', 'Ticketing'],
        placeholder: 'Add marketing skill...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['CMP', 'CSEP', 'CPCE', 'CMMP'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'eventHighlights', 'budgetManaged', 'teamSize', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // SALES
  // ============================================
  sales: {
    id: 'sales',
    name: 'Sales',
    icon: '📈',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Sales professional with expertise in...',
        required: true
      },
      {
        id: 'sales-specializations',
        label: 'Sales Specializations',
        type: 'skills',
        icon: '📈',
        defaultSkills: ['B2B Sales', 'B2C Sales', 'SaaS Sales', 'Enterprise Sales', 'Account Management'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'sales-skills',
        label: 'Sales Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Negotiation', 'Cold Calling', 'Lead Generation', 'Pipeline Management', 'Closing'],
        placeholder: 'Add sales skill...',
        required: true
      },
      {
        id: 'sales-tools',
        label: 'Sales Tools & CRMs',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Salesforce', 'HubSpot', 'Zoho CRM', 'Pipedrive', 'LinkedIn Sales Navigator'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Certified Sales Professional', 'Sandler Training', 'SPIN Selling', 'Solution Selling'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'quotaAchievement', 'keyAccounts', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // MARKETING
  // ============================================
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    icon: '📢',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Marketing professional with expertise in...',
        required: true
      },
      {
        id: 'marketing-specializations',
        label: 'Marketing Specializations',
        type: 'skills',
        icon: '📢',
        defaultSkills: ['Digital Marketing', 'Product Marketing', 'Brand Marketing', 'Content Marketing', 'Growth Marketing'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'marketing-skills',
        label: 'Marketing Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Market Research', 'Campaign Management', 'Analytics', 'Content Strategy', 'Budget Management'],
        placeholder: 'Add marketing skill...',
        required: true
      },
      {
        id: 'marketing-tools',
        label: 'Marketing Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Google Analytics', 'HubSpot', 'Hootsuite', 'Mailchimp', 'Canva', 'SEMrush'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Google Analytics Certified', 'HubSpot Certified', 'Facebook Blueprint', 'Google Ads Certified'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'campaignHighlights', 'metricsImproved', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // SEM / SEO
  // ============================================
  semSeo: {
    id: 'semSeo',
    name: 'SEO / SEM Specialist',
    icon: '🔍',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'SEO/SEM specialist with expertise in...',
        required: true
      },
      {
        id: 'seo-skills',
        label: 'SEO Skills',
        type: 'skills',
        icon: '🔍',
        defaultSkills: ['Keyword Research', 'On-Page SEO', 'Off-Page SEO', 'Technical SEO', 'Local SEO', 'E-commerce SEO'],
        placeholder: 'Add SEO skill...',
        required: true
      },
      {
        id: 'sem-skills',
        label: 'SEM / PPC Skills',
        type: 'skills',
        icon: '📊',
        defaultSkills: ['Google Ads', 'Bing Ads', 'Display Ads', 'Remarketing', 'Performance Max', 'A/B Testing'],
        placeholder: 'Add SEM skill...',
        required: true
      },
      {
        id: 'analytics-tools',
        label: 'Analytics & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Google Analytics', 'Google Search Console', 'SEMrush', 'Ahrefs', 'Moz', 'Screaming Frog', 'Keyword Planner'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Google Analytics Certified', 'Google Ads Certified', 'Bing Ads Certified', 'HubSpot SEO Certified'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'keywordsRanked', 'trafficGrowth', 'campaignROI', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // BRAND MANAGEMENT
  // ============================================
  brandManagement: {
    id: 'brandManagement',
    name: 'Brand Management',
    icon: '🏷️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Brand Manager with expertise in...',
        required: true
      },
      {
        id: 'brand-skills',
        label: 'Brand Management Skills',
        type: 'skills',
        icon: '🏷️',
        defaultSkills: ['Brand Strategy', 'Brand Positioning', 'Brand Identity', 'Brand Voice', 'Brand Architecture'],
        placeholder: 'Add brand skill...',
        required: true
      },
      {
        id: 'campaign-skills',
        label: 'Marketing Campaign Skills',
        type: 'skills',
        icon: '📢',
        defaultSkills: ['Campaign Management', 'Go-to-Market Strategy', 'Product Launch', 'Brand Awareness', 'Content Strategy'],
        placeholder: 'Add campaign skill...',
        required: false
      },
      {
        id: 'analytics-tools',
        label: 'Tools & Analytics',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Google Analytics', 'Brand Monitoring Tools', 'Social Listening Tools', 'Market Research Tools'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'brandsManaged', 'campaignHighlights', 'brandMetrics', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // SOCIAL MEDIA MANAGEMENT
  // ============================================
  socialMediaManagement: {
    id: 'socialMediaManagement',
    name: 'Social Media Management',
    icon: '📱',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Social Media Manager with expertise in...',
        required: true
      },
      {
        id: 'platforms',
        label: 'Social Media Platforms',
        type: 'skills',
        icon: '📱',
        defaultSkills: ['Instagram', 'TikTok', 'LinkedIn', 'YouTube', 'X/Twitter', 'Facebook', 'Pinterest', 'Snapchat'],
        placeholder: 'Add platform...',
        required: true
      },
      {
        id: 'social-media-skills',
        label: 'Social Media Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Content Creation', 'Community Management', 'Social Listening', 'Analytics', 'Influencer Marketing'],
        placeholder: 'Add social media skill...',
        required: true
      },
      {
        id: 'tools',
        label: 'Management Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Hootsuite', 'Buffer', 'Sprout Social', 'Later', 'Canva', 'Adobe Creative Suite'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Meta Certified', 'Google Digital Garage', 'Hootsuite Certified', 'HubSpot Certified'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'metricsGrowth', 'campaignHighlights', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // CONTENT MARKETING
  // ============================================
  contentMarketing: {
    id: 'contentMarketing',
    name: 'Content Marketing',
    icon: '✍️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Content Marketing professional with expertise in...',
        required: true
      },
      {
        id: 'content-types',
        label: 'Content Types',
        type: 'skills',
        icon: '✍️',
        defaultSkills: ['Blog Posts', 'Whitepapers', 'Ebooks', 'Case Studies', 'Video Scripts', 'Podcasts', 'Infographics'],
        placeholder: 'Add content type...',
        required: false
      },
      {
        id: 'content-skills',
        label: 'Content Marketing Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Content Strategy', 'SEO Writing', 'Editing', 'Storytelling', 'Content Distribution', 'Analytics'],
        placeholder: 'Add content skill...',
        required: true
      },
      {
        id: 'tools',
        label: 'Tools & Platforms',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['WordPress', 'SEO Tools', 'Email Marketing Tools', 'Canva', 'Grammarly', 'Trello'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'contentHighlights', 'metricsImproved', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // EMAIL MARKETING
  // ============================================
  emailMarketing: {
    id: 'emailMarketing',
    name: 'Email Marketing',
    icon: '📧',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Email Marketing specialist with expertise in...',
        required: true
      },
      {
        id: 'email-skills',
        label: 'Email Marketing Skills',
        type: 'skills',
        icon: '📧',
        defaultSkills: ['Email Campaign Design', 'Segmentation', 'A/B Testing', 'Automation', 'Deliverability', 'Analytics'],
        placeholder: 'Add email skill...',
        required: true
      },
      {
        id: 'platforms',
        label: 'Email Platforms',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Mailchimp', 'ConvertKit', 'ActiveCampaign', 'HubSpot', 'Klaviyo', 'Campaign Monitor'],
        placeholder: 'Add platform...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['HubSpot Certified', 'Mailchimp Certified', 'Email Marketing Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'campaignMetrics', 'listGrowth', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // CREATIVE / ART DIRECTION
  // ============================================
  creativeDesign: {
    id: 'creativeDesign',
    name: 'Creative Direction',
    icon: '🎨',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Creative professional with expertise in...',
        required: true
      },
      {
        id: 'creative-specializations',
        label: 'Creative Specializations',
        type: 'skills',
        icon: '🎨',
        defaultSkills: ['Art Direction', 'Visual Design', 'Brand Design', 'Creative Strategy', 'Design Thinking'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'software',
        label: 'Creative Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Photoshop', 'Illustrator', 'InDesign', 'Figma', 'Sketch', 'Procreate', 'After Effects'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'skills',
        label: 'Creative Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Typography', 'Color Theory', 'Composition', 'Storytelling', 'Concept Development', 'Pitching'],
        placeholder: 'Add creative skill...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Highlights',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'client', 'impact'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights', 'creativeAchievements'],
        required: true
      }
    ]
  },

  // ============================================
  // UI/UX DESIGN
  // ============================================
  uiUxDesign: {
    id: 'uiUxDesign',
    name: 'UI/UX Design',
    icon: '🎨',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'UI/UX Designer with expertise in...',
        required: true
      },
      {
        id: 'ux-skills',
        label: 'UX Skills',
        type: 'skills',
        icon: '🔍',
        defaultSkills: ['User Research', 'User Personas', 'Information Architecture', 'Wireframing', 'Prototyping', 'Usability Testing'],
        placeholder: 'Add UX skill...',
        required: true
      },
      {
        id: 'ui-skills',
        label: 'UI Skills',
        type: 'skills',
        icon: '🎨',
        defaultSkills: ['Visual Design', 'Typography', 'Color Theory', 'Design Systems', 'Interaction Design', 'Iconography'],
        placeholder: 'Add UI skill...',
        required: true
      },
      {
        id: 'tools',
        label: 'Design Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Zeplin', 'UX Research Tools'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Google UX Design Certificate', 'Nielsen Norman Certified', 'Interaction Design Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'toolsUsed', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'designHighlights', 'impactMetrics'],
        required: true
      }
    ]
  },

  // ============================================
  // GRAPHIC DESIGN
  // ============================================
  graphicDesign: {
    id: 'graphicDesign',
    name: 'Graphic Design',
    icon: '✏️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Graphic Designer with expertise in...',
        required: true
      },
      {
        id: 'design-specializations',
        label: 'Design Specializations',
        type: 'skills',
        icon: '✏️',
        defaultSkills: ['Brand Identity', 'Packaging', 'Typography', 'Editorial Design', 'Print Design'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'software',
        label: 'Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Photoshop', 'Illustrator', 'InDesign', 'Figma', 'Lightroom', 'Premiere Pro', 'After Effects'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'skills',
        label: 'Design Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Typography', 'Color Theory', 'Layout', 'Composition', 'Visual Storytelling'],
        placeholder: 'Add design skill...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Highlights',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'client', 'impact'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // ANIMATION & VFX
  // ============================================
  animationVfx: {
    id: 'animationVfx',
    name: 'Animation & VFX',
    icon: '🎬',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Animation & VFX professional with expertise in...',
        required: true
      },
      {
        id: 'animation-types',
        label: 'Animation Types',
        type: 'skills',
        icon: '🎬',
        defaultSkills: ['3D Animation', '2D Animation', 'Motion Graphics', 'Stop Motion', 'Character Animation'],
        placeholder: 'Add animation type...',
        required: false
      },
      {
        id: 'vfx-skills',
        label: 'VFX Skills',
        type: 'skills',
        icon: '✨',
        defaultSkills: ['Compositing', 'Visual Effects', 'CGI', 'Green Screen', 'Match Moving', 'Rotoscoping'],
        placeholder: 'Add VFX skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Animation Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Blender', 'Maya', '3ds Max', 'Houdini', 'After Effects', 'Nuke', 'Cinema 4D', 'ZBrush'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio / Showreel',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'softwareUsed', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // GAME DEVELOPMENT & DESIGN
  // ============================================
  gameDevelopment: {
    id: 'gameDevelopment',
    name: 'Game Development & Design',
    icon: '🎮',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Game Developer/Designer with expertise in...',
        required: true
      },
      {
        id: 'game-specializations',
        label: 'Game Development Specializations',
        type: 'skills',
        icon: '🎮',
        defaultSkills: ['Game Programming', 'Game Design', 'Level Design', 'Game Art', 'Game Audio'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'programming-languages',
        label: 'Programming Languages',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['C++', 'C#', 'Python', 'JavaScript', 'Lua', 'GDScript'],
        placeholder: 'Add programming language...',
        required: true
      },
      {
        id: 'game-engines',
        label: 'Game Engines',
        type: 'skills',
        icon: '⚙️',
        defaultSkills: ['Unity', 'Unreal Engine', 'Godot', 'CryEngine', 'GameMaker'],
        placeholder: 'Add game engine...',
        required: true
      },
      {
        id: 'design-software',
        label: 'Design Software',
        type: 'skills',
        icon: '🎨',
        defaultSkills: ['Blender', 'Maya', 'Photoshop', 'Substance Painter', 'ZBrush', 'Figma'],
        placeholder: 'Add design software...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'games',
        label: 'Games & Projects',
        type: 'projects',
        icon: '🚀',
        fields: ['gameName', 'description', 'engine', 'role', 'platforms', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'gamesWorkedOn'],
        required: true
      }
    ]
  },

  // ============================================
  // MOTION GRAPHICS
  // ============================================
  motionGraphics: {
    id: 'motionGraphics',
    name: 'Motion Graphics',
    icon: '🎞️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Motion Graphics professional with expertise in...',
        required: true
      },
      {
        id: 'motion-types',
        label: 'Motion Graphics Types',
        type: 'skills',
        icon: '🎞️',
        defaultSkills: ['2D Motion', '3D Motion', 'Typography', 'Title Design', 'Infographics', 'VFX'],
        placeholder: 'Add motion type...',
        required: false
      },
      {
        id: 'software',
        label: 'Motion Graphics Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['After Effects', 'Premiere Pro', 'Cinema 4D', 'Blender', 'Nuke', 'Final Cut Pro'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'skills',
        label: 'Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Keyframing', 'Compositing', 'Scripting', 'Storyboarding', 'Editing', 'Sound Design'],
        placeholder: 'Add skill...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio / Showreel',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'softwareUsed', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // PRODUCT DESIGN
  // ============================================
  productDesign: {
    id: 'productDesign',
    name: 'Product Design',
    icon: '📱',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Product Designer with expertise in...',
        required: true
      },
      {
        id: 'design-specializations',
        label: 'Product Design Specializations',
        type: 'skills',
        icon: '📱',
        defaultSkills: ['Consumer Electronics', 'Furniture', 'Packaging', 'Industrial Products', 'Digital Products'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'design-process',
        label: 'Design Process Skills',
        type: 'skills',
        icon: '🔄',
        defaultSkills: ['User Research', 'Concept Development', 'Prototyping', 'Testing', 'Manufacturing Knowledge'],
        placeholder: 'Add design process skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['SolidWorks', 'Figma', 'Adobe XD', 'Rhino', 'Sketch', 'Procreate', 'Keyshot'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'toolsUsed', 'manufacturingDetails'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'productHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // FASHION DESIGN
  // ============================================
  fashionDesign: {
    id: 'fashionDesign',
    name: 'Fashion Design',
    icon: '👗',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Fashion Designer with expertise in...',
        required: true
      },
      {
        id: 'fashion-specializations',
        label: 'Fashion Specializations',
        type: 'skills',
        icon: '👗',
        defaultSkills: ['Womenswear', 'Menswear', 'Accessories', 'Footwear', 'Haute Couture'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'design-skills',
        label: 'Design Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Sketching', 'Pattern Making', 'Draping', 'Technical Drawing', 'Color Theory', 'Fabric Knowledge'],
        placeholder: 'Add design skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Fashion Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Adobe Illustrator', 'Photoshop', 'CLO 3D', 'Optitex', 'Gerber'],
        placeholder: 'Add software...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Highlights',
        type: 'projects',
        icon: '📁',
        fields: ['collectionName', 'description', 'inspiration', 'techniques', 'showcased'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'collectionHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // INDUSTRIAL DESIGN
  // ============================================
  industrialDesign: {
    id: 'industrialDesign',
    name: 'Industrial Design',
    icon: '🏭',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Industrial Designer with expertise in...',
        required: true
      },
      {
        id: 'product-areas',
        label: 'Product Areas',
        type: 'skills',
        icon: '🏭',
        defaultSkills: ['Consumer Products', 'Medical Devices', 'Automotive', 'Furniture', 'Electronics'],
        placeholder: 'Add product area...',
        required: false
      },
      {
        id: 'design-process',
        label: 'Design Process',
        type: 'skills',
        icon: '🔄',
        defaultSkills: ['Sketching', '3D Modeling', 'Prototyping', 'User Testing', 'Manufacturing Design'],
        placeholder: 'Add design process skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['SolidWorks', 'Rhino', 'AutoCAD', 'Fusion 360', 'Keyshot', 'Adobe Creative Suite'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'description', 'role', 'manufacturingProcess', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'productHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // INTERIOR DESIGN
  // ============================================
  interiorDesign: {
    id: 'interiorDesign',
    name: 'Interior Design',
    icon: '🛋️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Interior Designer with expertise in...',
        required: true
      },
      {
        id: 'project-types',
        label: 'Project Types',
        type: 'skills',
        icon: '🛋️',
        defaultSkills: ['Residential', 'Commercial', 'Hospitality', 'Healthcare', 'Retail'],
        placeholder: 'Add project type...',
        required: false
      },
      {
        id: 'design-skills',
        label: 'Interior Design Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Space Planning', 'Color Theory', 'Materials Selection', 'Lighting Design', 'Furniture Design'],
        placeholder: 'Add design skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['AutoCAD', 'SketchUp', 'Revit', '3ds Max', 'Enscape', 'Photoshop', 'InDesign'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['NCIDQ Certified', 'LEED AP', 'WELL Certified', 'CID'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'type', 'description', 'softwareUsed', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // LANDSCAPE ARCHITECTURE
  // ============================================
  landscapeArchitecture: {
    id: 'landscapeArchitecture',
    name: 'Landscape Architecture',
    icon: '🌳',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Landscape Architect with expertise in...',
        required: true
      },
      {
        id: 'project-types',
        label: 'Project Types',
        type: 'skills',
        icon: '🌳',
        defaultSkills: ['Parks', 'Gardens', 'Urban Design', 'Residential', 'Commercial', 'Ecological Restoration'],
        placeholder: 'Add project type...',
        required: false
      },
      {
        id: 'design-skills',
        label: 'Landscape Design Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Site Planning', 'Plant Selection', 'Hardscape Design', 'Water Management', 'Sustainable Design'],
        placeholder: 'Add design skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Design Software',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['AutoCAD', 'SketchUp', 'ArcGIS', 'Lumion', 'Photoshop', 'InDesign', 'Rhino'],
        placeholder: 'Add software...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Licensed Landscape Architect', 'LEED AP', 'SITES AP', 'ASLA Member'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'portfolio',
        label: 'Portfolio Projects',
        type: 'projects',
        icon: '📁',
        fields: ['projectName', 'type', 'description', 'softwareUsed', 'outcome'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities', 'projectHighlights'],
        required: true
      }
    ]
  },

  // ============================================
  // WELLNESS COACHING
  // ============================================
  wellnessCoaching: {
    id: 'wellnessCoaching',
    name: 'Wellness Coaching',
    icon: '🧘',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Wellness Coach with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Certified Wellness Coach', 'Health Coach Certification', 'NCCPT', 'ACE Certified', 'NASM Certified'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'wellness-areas',
        label: 'Wellness Areas',
        type: 'skills',
        icon: '🧘',
        defaultSkills: ['Nutrition', 'Fitness', 'Stress Management', 'Mindfulness', 'Sleep Health', 'Mental Wellness'],
        placeholder: 'Add wellness area...',
        required: false
      },
      {
        id: 'coaching-skills',
        label: 'Coaching Skills',
        type: 'skills',
        icon: '💬',
        defaultSkills: ['Client Assessment', 'Goal Setting', 'Accountability', 'Motivational Interviewing', 'Behavior Change'],
        placeholder: 'Add coaching skill...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🎯',
        defaultSkills: ['Corporate Wellness', 'Weight Management', 'Women\'s Health', 'Senior Wellness', 'Athletic Performance'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'clientTypes', 'responsibilities', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // NON-PROFIT / NGO
  // ============================================
  nonprofit: {
    id: 'nonprofit',
    name: 'Non-Profit / NGO',
    icon: '🤝',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Non-Profit professional with expertise in...',
        required: true
      },
      {
        id: 'nonprofit-areas',
        label: 'Non-Profit Areas',
        type: 'skills',
        icon: '🤝',
        defaultSkills: ['Program Management', 'Grant Writing', 'Fundraising', 'Community Outreach', 'Advocacy'],
        placeholder: 'Add non-profit area...',
        required: false
      },
      {
        id: 'fundraising-skills',
        label: 'Fundraising Skills',
        type: 'skills',
        icon: '💰',
        defaultSkills: ['Grant Writing', 'Donor Cultivation', 'Event Fundraising', 'Corporate Sponsorships', 'Campaigns'],
        placeholder: 'Add fundraising skill...',
        required: true
      },
      {
        id: 'program-skills',
        label: 'Program Skills',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Program Development', 'Project Management', 'Monitoring & Evaluation', 'Community Engagement'],
        placeholder: 'Add program skill...',
        required: true
      },
      {
        id: 'software',
        label: 'Software & Tools',
        type: 'skills',
        icon: '💻',
        defaultSkills: ['Donor CRM', 'Salesforce', 'Google Workspace', 'Canva', 'Mailchimp'],
        placeholder: 'Add software...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Grant Writing Certification', 'Project Management', 'Non-Profit Management'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'responsibilities', 'impactMetrics', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // PUBLIC ADMINISTRATION
  // ============================================
  publicAdministration: {
    id: 'publicAdministration',
    name: 'Public Administration',
    icon: '📋',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Public Administration professional with expertise in...',
        required: true
      },
      {
        id: 'public-service-areas',
        label: 'Public Service Areas',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Policy Development', 'Public Finance', 'Program Management', 'Community Relations', 'Government Affairs'],
        placeholder: 'Add public service area...',
        required: false
      },
      {
        id: 'administration-skills',
        label: 'Administration Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Budget Management', 'Strategic Planning', 'Performance Management', 'Stakeholder Engagement'],
        placeholder: 'Add administration skill...',
        required: true
      },
      {
        id: 'policy-skills',
        label: 'Policy Skills',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Policy Analysis', 'Legislative Affairs', 'Regulatory Compliance', 'Public Policy Research'],
        placeholder: 'Add policy skill...',
        required: false
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['MPA', 'Certified Public Manager', 'Government Leadership Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'responsibilities', 'policyInitiatives', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // INTERNATIONAL RELATIONS
  // ============================================
  internationalRelations: {
    id: 'internationalRelations',
    name: 'International Relations',
    icon: '🌍',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'International Relations professional with expertise in...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🌍',
        defaultSkills: ['Diplomacy', 'International Security', 'Global Governance', 'Political Economy', 'Human Rights'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'skills',
        label: 'International Relations Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Political Analysis', 'Strategic Planning', 'Crisis Management', 'Diplomatic Negotiation'],
        placeholder: 'Add skill...',
        required: true
      },
      {
        id: 'languages',
        label: 'Languages',
        type: 'skills',
        icon: '🗣️',
        defaultSkills: ['English', 'French', 'Spanish', 'Arabic', 'Chinese', 'Russian'],
        placeholder: 'Add language...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'responsibilities', 'internationalProjects', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // DISASTER MANAGEMENT
  // ============================================
  disasterManagement: {
    id: 'disasterManagement',
    name: 'Disaster Management',
    icon: '🚨',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Disaster Management professional with expertise in...',
        required: true
      },
      {
        id: 'disaster-types',
        label: 'Disaster Types',
        type: 'skills',
        icon: '🚨',
        defaultSkills: ['Natural Disasters', 'Man-Made Disasters', 'Pandemics', 'Industrial Accidents', 'Terrorism'],
        placeholder: 'Add disaster type...',
        required: false
      },
      {
        id: 'management-skills',
        label: 'Management Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Emergency Planning', 'Crisis Management', 'Incident Command', 'Risk Assessment', 'Resource Allocation'],
        placeholder: 'Add management skill...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['FEMA Certified', 'ICS Training', 'EMS Certification', 'Crisis Management Certification'],
        placeholder: 'Add certification...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'responsibilities', 'disasterResponse', 'impact'],
        required: true
      }
    ]
  },

  // ============================================
  // IMMIGRATION SERVICES
  // ============================================
  immigrationServices: {
    id: 'immigrationServices',
    name: 'Immigration Services',
    icon: '🛂',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Immigration Services professional with expertise in...',
        required: true
      },
      {
        id: 'service-areas',
        label: 'Service Areas',
        type: 'skills',
        icon: '🛂',
        defaultSkills: ['Visa Processing', 'Immigration Law', 'Refugee Resettlement', 'Citizenship Services', 'Compliance'],
        placeholder: 'Add service area...',
        required: false
      },
      {
        id: 'skills',
        label: 'Immigration Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Case Management', 'Documentation', 'Regulatory Compliance', 'Client Counseling', 'Filing Procedures'],
        placeholder: 'Add skill...',
        required: true
      },
      {
        id: 'regulatory',
        label: 'Regulatory Knowledge',
        type: 'skills',
        icon: '⚖️',
        defaultSkills: ['USCIS Procedures', 'Immigration Law', 'Consular Processing', 'Asylum Law'],
        placeholder: 'Add regulatory knowledge...',
        required: true
      },
      {
        id: 'languages',
        label: 'Languages',
        type: 'skills',
        icon: '🗣️',
        defaultSkills: ['English', 'Spanish', 'French', 'Mandarin'],
        placeholder: 'Add language...',
        required: false
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year', 'honors'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['organization', 'position', 'dates', 'responsibilities', 'caseTypes', 'achievements'],
        required: true
      }
    ]
  },

  // ============================================
  // ELECTRICIAN
  // ============================================
  electrician: {
    id: 'electrician',
    name: 'Electrician',
    icon: '⚡',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Electrician with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Licensed Electrician', 'Journeyman', 'Master Electrician', 'OSHA Certified'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '⚡',
        defaultSkills: ['Residential', 'Commercial', 'Industrial', 'Maintenance', 'Low Voltage'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'electrical-skills',
        label: 'Electrical Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Wiring', 'Circuit Installation', 'Panel Upgrades', 'Lighting', 'Safety Inspections'],
        placeholder: 'Add electrical skill...',
        required: true
      },
      {
        id: 'code-knowledge',
        label: 'Code & Safety Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['NEC', 'Local Codes', 'Safety Protocols', 'Lockout/Tagout', 'Arc Flash Safety'],
        placeholder: 'Add code knowledge...',
        required: false
      },
      {
        id: 'tools',
        label: 'Tools & Equipment',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Multimeter', 'Wire Strippers', 'Pliers', 'Screwdrivers', 'Power Tools'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education & Apprenticeship',
        type: 'education',
        icon: '🎓',
        fields: ['program', 'institution', 'year', 'hoursCompleted'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'projectTypes', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // PLUMBING
  // ============================================
  plumbing: {
    id: 'plumbing',
    name: 'Plumbing',
    icon: '🔧',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Plumber with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['Licensed Plumber', 'Journeyman', 'Master Plumber', 'Backflow Certified'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Residential', 'Commercial', 'Industrial', 'New Construction', 'Repair'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'plumbing-skills',
        label: 'Plumbing Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Pipe Installation', 'Drain Cleaning', 'Water Heater Installation', 'Fixture Repair', 'Sewage Systems'],
        placeholder: 'Add plumbing skill...',
        required: true
      },
      {
        id: 'code-knowledge',
        label: 'Code Knowledge',
        type: 'skills',
        icon: '📋',
        defaultSkills: ['Uniform Plumbing Code', 'IPC', 'Local Codes', 'Safety Standards'],
        placeholder: 'Add code knowledge...',
        required: false
      },
      {
        id: 'tools',
        label: 'Tools & Equipment',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Pipe Wrenches', 'Plungers', 'Augers', 'Pipe Cutters', 'Welding Equipment'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education & Apprenticeship',
        type: 'education',
        icon: '🎓',
        fields: ['program', 'institution', 'year', 'hoursCompleted'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'projectTypes', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // WELDING
  // ============================================
  welding: {
    id: 'welding',
    name: 'Welding',
    icon: '🔥',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Welder with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['AWS Certified', 'CWB Certified', 'API 1104', 'ASME Sec IX'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'welding-processes',
        label: 'Welding Processes',
        type: 'skills',
        icon: '🔥',
        defaultSkills: ['MIG', 'TIG', 'Stick', 'Flux-Cored', 'Submerged Arc'],
        placeholder: 'Add welding process...',
        required: true
      },
      {
        id: 'materials',
        label: 'Materials Welded',
        type: 'skills',
        icon: '🧪',
        defaultSkills: ['Steel', 'Stainless Steel', 'Aluminum', 'Cast Iron', 'Alloys'],
        placeholder: 'Add material...',
        required: false
      },
      {
        id: 'positions',
        label: 'Welding Positions',
        type: 'skills',
        icon: '📐',
        defaultSkills: ['1G/Flat', '2G/Horizontal', '3G/Vertical', '4G/Overhead', 'Pipe Positions'],
        placeholder: 'Add position...',
        required: false
      },
      {
        id: 'tools',
        label: 'Tools & Equipment',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Welding Machines', 'Grinders', 'Cutting Torches', 'Safety Equipment'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education & Training',
        type: 'education',
        icon: '🎓',
        fields: ['program', 'institution', 'year', 'certifications'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'projectTypes', 'materialsUsed', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // HVAC
  // ============================================
  hvac: {
    id: 'hvac',
    name: 'HVAC',
    icon: '❄️',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'HVAC professional with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Licenses & Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['HVAC License', 'EPA Section 608', 'NATE Certified', 'OSHA Certified'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '❄️',
        defaultSkills: ['Residential', 'Commercial', 'Industrial', 'Refrigeration', 'Air Quality'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'hvac-skills',
        label: 'HVAC Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Installation', 'Repair', 'Maintenance', 'Troubleshooting', 'System Design'],
        placeholder: 'Add HVAC skill...',
        required: true
      },
      {
        id: 'systems',
        label: 'Systems Experience',
        type: 'skills',
        icon: '🏗️',
        defaultSkills: ['AC Units', 'Heat Pumps', 'Furnaces', 'Boilers', 'Chillers', 'RTUs'],
        placeholder: 'Add system...',
        required: false
      },
      {
        id: 'tools',
        label: 'Tools & Equipment',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Manifold Gauges', 'Multimeter', 'Refrigerant Tools', 'Duct Tools'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education & Apprenticeship',
        type: 'education',
        icon: '🎓',
        fields: ['program', 'institution', 'year', 'hoursCompleted'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'projectTypes', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // AUTOMOTIVE TECHNICIAN
  // ============================================
  automotiveTechnician: {
    id: 'automotiveTechnician',
    name: 'Automotive Technician',
    icon: '🚗',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Automotive Technician with expertise in...',
        required: true
      },
      {
        id: 'certifications',
        label: 'Certifications',
        type: 'skills',
        icon: '📜',
        defaultSkills: ['ASE Certified', 'Manufacturer Certified', 'Advanced Level Specialist'],
        placeholder: 'Add certification...',
        required: true
      },
      {
        id: 'specializations',
        label: 'Specializations',
        type: 'skills',
        icon: '🚗',
        defaultSkills: ['Engine Repair', 'Transmission', 'Brakes', 'Electrical Systems', 'Diagnostics'],
        placeholder: 'Add specialization...',
        required: false
      },
      {
        id: 'automotive-skills',
        label: 'Automotive Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: ['Diagnostics', 'Engine Repair', 'Brake Systems', 'Suspension', 'Electrical Wiring'],
        placeholder: 'Add automotive skill...',
        required: true
      },
      {
        id: 'tools',
        label: 'Tools & Equipment',
        type: 'skills',
        icon: '🔧',
        defaultSkills: ['Diagnostic Scanners', 'Power Tools', 'Lifts', 'Multimeter', 'Welding Equipment'],
        placeholder: 'Add tool...',
        required: false
      },
      {
        id: 'education',
        label: 'Education & Training',
        type: 'education',
        icon: '🎓',
        fields: ['program', 'institution', 'year', 'certifications'],
        required: true
      },
      {
        id: 'experience',
        label: 'Professional Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'vehicleTypes', 'specialty', 'responsibilities'],
        required: true
      }
    ]
  },

  // ============================================
  // GENERIC / DEFAULT (Fallback)
  // ============================================
  generic: {
    id: 'generic',
    name: 'Generic Resume',
    icon: '📄',
    sections: [
      {
        id: 'summary',
        label: 'Professional Summary',
        type: 'textarea',
        placeholder: 'Professional with experience in...',
        required: true
      },
      {
        id: 'skills',
        label: 'Skills',
        type: 'skills',
        icon: '🛠️',
        defaultSkills: [],
        placeholder: 'Add skill...',
        required: true
      },
      {
        id: 'experience',
        label: 'Experience',
        type: 'experience',
        icon: '💼',
        fields: ['company', 'position', 'dates', 'responsibilities'],
        required: true
      },
      {
        id: 'education',
        label: 'Education',
        type: 'education',
        icon: '🎓',
        fields: ['degree', 'institution', 'year'],
        required: true
      }
    ]
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getIndustrySections = (industryId) => {
  return industrySections[industryId] || industrySections.generic;
};

export const getDefaultSections = () => {
  return industrySections.generic;
};

export const getAllIndustrySections = () => {
  return industrySections;
};

export const getIndustrySectionKeys = () => {
  return Object.keys(industrySections);
};

export const getIndustryNames = () => {
  return Object.values(industrySections).map(industry => ({
    id: industry.id,
    name: industry.name,
    icon: industry.icon
  }));
};